import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'
import { NotificationList } from '@/features/notifications/NotificationList'
import { fetchUnreadCount } from '@/features/notifications/api'
import type { ApiError } from '@/lib/api/client'

/**
 * The bell, on every screen.
 *
 * The count is polled on its own cheap endpoint rather than derived from the
 * list, so the badge stays current without fetching fifty rows and their
 * actors every half minute. The list is only fetched when the panel opens.
 */
export function NotificationBell() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  const unread = useQuery<number, ApiError>({
    queryKey: ['notifications-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 30_000,
    // A tab left open all afternoon should be current when it comes back.
    refetchOnWindowFocus: true,
    retry: false,
  })

  // A panel that only closes by its own button is a panel people click around.
  useEffect(() => {
    if (!open) return

    const dismiss = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false)

        return
      }

      if (container.current !== null && !container.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', dismiss)
    document.addEventListener('keydown', dismiss)

    return () => {
      document.removeEventListener('mousedown', dismiss)
      document.removeEventListener('keydown', dismiss)
    }
  }, [open])

  const count = unread.data ?? 0

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={count > 0 ? t('notifications.unread', { count }) : t('notifications.open')}
        aria-expanded={open}
        title={t('notifications.title')}
        className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 relative rounded p-1.5"
      >
        <Icon name="bell" className="size-4" />
        {count > 0 ? (
          <span className="bg-brand-600 absolute -top-0.5 -end-0.5 min-w-4 rounded-full px-1 text-center font-mono text-[9px] leading-4 text-white tabular-nums">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="border-line bg-surface absolute end-0 z-50 mt-1.5 max-h-[70vh] w-80 overflow-y-auto rounded-md border shadow-xl">
          <header className="border-line flex items-center gap-2 border-b px-4 py-2">
            <h2 className="text-ink-900 text-[13px] font-semibold">{t('notifications.title')}</h2>
          </header>

          <NotificationList onNavigate={() => setOpen(false)} />
        </div>
      ) : null}
    </div>
  )
}
